import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import triggersHelper from "../../../../API/Triggers/triggersHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import TriggerSettingsView from "../SubComponents/TriggerSettingsView";

const buildEventConfig = (v) => {
  if (v.eventSourceType === "NATIVE") {
    return JSON.stringify({
      eventType: v.native?.eventType,
      eventCategory: v.native?.eventCategory || null,
      dataFilter: (v.native?.dataFilter || []).filter(
        (r) => r?.field && r?.field.trim() !== ""
      ),
    });
  }
  return JSON.stringify({
    provider: v.external?.provider,
    providerEvent: v.external?.providerEvent,
    authorisationType: v.external?.authorisationType || null,
    authorisationID: v.external?.authorisationID || null,
    acquisitionMode: v.external?.acquisitionMode || "POLLING",
    pollConfig: {
      intervalSeconds: v.external?.pollConfig?.intervalSeconds || 300,
      endpoint: v.external?.pollConfig?.endpoint || null,
      jsonPath: v.external?.pollConfig?.jsonPath || null,
    },
    integrationConfig: v.external?.integrationConfig || null,
  });
};

const parseEventConfig = (eventConfig) => {
  if (!eventConfig) return {};
  try {
    return typeof eventConfig === "string"
      ? JSON.parse(eventConfig)
      : eventConfig;
  } catch (e) {
    console.warn("Failed to parse trigger eventConfig", e);
    return {};
  }
};

const TriggersSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onTriggerCreated,
      selectedId,
      setShowTriggerModal,
      setInitialMapValues,
      onTriggerDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace } = useWorkspaces();

    const validationSchema = AppForms.yup.object().shape({
      name: AppForms.yup.string().required().label("Name").max(255),
      description: AppForms.yup
        .string()
        .nullable()
        .label("Description")
        .max(2048),
      forcedPrompt: AppForms.yup
        .string()
        .nullable()
        .label("Forced Prompt")
        .max(1024),
      agentID: AppForms.yup.string().required().label("Agent"),
      customPromptID: AppForms.yup
        .string()
        .nullable()
        .label("Prompt")
        .test(
          "prompt-required",
          "Either select a prompt or provide a forced prompt",
          function (value) {
            const { forcedPrompt } = this.parent;
            return (
              (value && value.trim() !== "") ||
              (forcedPrompt && forcedPrompt.trim() !== "")
            );
          }
        ),
      eventSourceType: AppForms.yup
        .string()
        .required()
        .oneOf(["NATIVE", "EXTERNAL"])
        .label("Event Source"),
      isActive: AppForms.yup.boolean().nullable().label("Active"),
    });

    const navigateBack = () => {
      setDisplayedView && setDisplayedView(backTo);
    };

    const identifyOptByID = (id) => state.triggers?.find((u) => u?.id === id);

    const handleDeletion = async (id) => {
      try {
        await triggersHelper.deleteTrigger(id);
        const workspaceID = getActiveWorkspace()?.id;
        if (workspaceID) {
          let fresh = await triggersHelper.getTriggersByWorkspaceID(
            workspaceID
          );
          fresh = fresh?.data?.triggersByWorkspace?.items?.filter(
            (u) => u?._deleted != true
          );
          dispatch("SET_TRIGGERS", fresh || []);
        }
        AppToasty.show(t("triggers.deletionSuccess"), { type: "success" });
        setShowTriggerModal?.(false);
        onTriggerDeleted();
      } catch (error) {
        console.error(error);
        AppToasty.show(t("triggers.deletionFailed"), { type: "danger" });
      }
    };

    const handleTriggerItemSave = async (submitted) => {
      const workspaceID = getActiveWorkspace()?.id;
      let v = { ...submitted };
      const eventConfig = buildEventConfig(v);
      const status = v.isActive ? "ARMED" : "PAUSED";
      let newTriggers = state.triggers?.length > 0 ? [...state.triggers] : [];

      try {
        let result;
        if (isEdit) {
          const updatePayload = [
            { property: "name", value: v.name },
            { property: "description", value: v.description },
            { property: "forcedPrompt", value: v.forcedPrompt || null },
            { property: "agentID", value: v.agentID },
            { property: "customPromptID", value: v.customPromptID || null },
            { property: "eventSourceType", value: v.eventSourceType },
            { property: "eventConfig", value: eventConfig },
            { property: "isActive", value: v.isActive },
            { property: "status", value: status },
            { property: "executionConfig", value: v.executionConfig || null },
            {
              property: "notificationConfig",
              value: v.notificationConfig || null,
            },
            { property: "retryConfig", value: v.retryConfig || null },
            { property: "metadata", value: v.metadata || null },
          ];
          result = await triggersHelper.updateTrigger(
            selectedId,
            updatePayload
          );
          result = result?.data?.updateTrigger;
          const updateIndex = newTriggers.findIndex(
            (u) => u?.id === result?.id
          );
          if (updateIndex >= 0) newTriggers[updateIndex] = result;
        } else {
          const createPayload = {
            name: v.name,
            description: v.description,
            forcedPrompt: v.forcedPrompt || null,
            agentID: v.agentID,
            customPromptID: v.customPromptID || null,
            eventSourceType: v.eventSourceType,
            eventConfig,
            isActive: v.isActive,
            status,
            workspaceID,
            executionConfig: v.executionConfig || null,
            notificationConfig: v.notificationConfig || null,
            retryConfig: v.retryConfig || null,
            metadata: null,
          };
          result = await triggersHelper.createTrigger(createPayload);
          result = result?.data?.createTrigger;
          newTriggers.push(result);
        }

        dispatch("SET_TRIGGERS", [...newTriggers]);

        try {
          const wsID = getActiveWorkspace()?.id;
          if (wsID) {
            let fresh = await triggersHelper.getTriggersByWorkspaceID(wsID);
            fresh = fresh?.data?.triggersByWorkspace?.items?.filter(
              (u) => u?._deleted != true
            );
            dispatch("SET_TRIGGERS", fresh || []);
          }
        } catch (refreshError) {
          console.error("Error refreshing triggers:", refreshError);
        }

        setShowTriggerModal?.(false);
        backTo && navigateBack();
        const savedId = result?.id;
        onTriggerCreated &&
          onTriggerCreated("success", isEdit, savedId, submitted);
      } catch (error) {
        console.error(error);
        onTriggerCreated && onTriggerCreated("error", isEdit, null, submitted);
      } finally {
        setShowTriggerModal?.(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Trigger : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}
          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleTriggerItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <TriggerSettingsView
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state?.activeWorkspaceID,
        JSON.stringify(initialMapValues),
        JSON.stringify(state.triggers),
        isEdit,
        AppToasty,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default TriggersSettings;
export { parseEventConfig, buildEventConfig };
